import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiService } from './api.service';
import type { NotificationModel } from '../models';
import type { PagedResponse } from '../models';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly api = inject(ApiService);

  private readonly _unseenCount = signal(0);
  private countLoaded = false;

  /** Unseen badge count. Loaded once via `loadUnseenCount()`, then kept in sync locally. */
  readonly unseenCount = this._unseenCount.asReadonly();

  getNotifications(page = 1, size = 20): Observable<PagedResponse<NotificationModel>> {
    return this.api.get<PagedResponse<NotificationModel>>(
      'notifications/getNotificationsForUser',
      { params: { page, size } },
    );
  }

  /** Fetches the unseen count from the API only the first time; later calls are no-ops. */
  loadUnseenCount(): void {
    if (this.countLoaded) return;
    this.countLoaded = true;
    this.api.get<number>('notifications/getUnseenNotificationCount').subscribe({
      next: count => this._unseenCount.set(count),
      error: () => { this.countLoaded = false; },
    });
  }

  /** Ids already subtracted from the badge (the list is reloaded on every open and may still report them unseen). */
  private readonly seenIds = new Set<number>();

  /** The user opened the list and saw these unseen notifications: lower the badge locally, no refetch. */
  markSeen(ids: number[]): void {
    const fresh = ids.filter(id => !this.seenIds.has(id));
    fresh.forEach(id => this.seenIds.add(id));
    if (fresh.length) this._unseenCount.update(c => Math.max(0, c - fresh.length));
  }

  /** Deletes a notification; if it was unseen the badge count is decremented locally. */
  deleteNotification(id: number, wasUnseen: boolean): Observable<void> {
    return this.api.delete<void>(`notifications/${id}`).pipe(
      tap(() => {
        if (wasUnseen && !this.seenIds.has(id)) this._unseenCount.update(c => Math.max(0, c - 1));
        this.seenIds.delete(id);
      }),
    );
  }

  /** Forget cached state (call on logout so the next user refetches). */
  reset(): void {
    this.countLoaded = false;
    this.seenIds.clear();
    this._unseenCount.set(0);
  }
}
